/**
 * UX Sprint 1 — Clean project header.
 *
 * Replaces the cluttered header on ProjectBoard with:
 *   - Client breadcrumb + project title + trimmed brief
 *   - Current stage pill
 *   - Primary CTA: "Project Assistant" (opens the assistant drawer)
 *   - Secondary: "More Tools" (opens advanced drawer)
 *   - The original Cost/Mode/Archive/Report controls live in a
 *     small `…More` dropdown so the header stays scannable.
 *
 * NOTE: All existing data-testids on the controls passed in via
 * `extraControls` (ModeToggle, CostPill, DownloadBtn, ArchiveProjectButton)
 * are preserved — we don't unmount any of them, we just relocate them
 * into the dropdown.
 */
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, SlidersHorizontal, MoreHorizontal } from "lucide-react";

export default function ProjectHeader({
    project,
    client,
    currentStageLabel,
    onOpenAssistant,
    onOpenMoreTools,
    extraControls,
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener("click", onDoc);
        return () => window.removeEventListener("click", onDoc);
    }, [open]);

    return (
        <div
            data-testid="project-header-clean"
            className="px-6 lg:px-10 py-6 border-b border-zinc-900 bg-[#0a0a0a]"
        >
            <div className="flex items-start justify-between gap-6 flex-wrap">
                {/* Left: identity */}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs font-mono text-zinc-500 uppercase tracking-wider mb-2">
                        <Link
                            to={`/clients/${client?.id || ""}`}
                            className="hover:text-white"
                            data-testid="project-header-client-link"
                        >
                            {client?.company_name || "client"}
                        </Link>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-zinc-700">
                            {(project?.project_type || "project").replaceAll("_", " ")}
                        </span>
                    </div>
                    <h1
                        data-testid="project-header-title"
                        className="font-display text-3xl sm:text-4xl font-medium tracking-tight"
                    >
                        {project?.title || "Untitled project"}
                    </h1>
                    {project?.brief && (
                        <p className="text-sm text-zinc-500 mt-2 max-w-3xl line-clamp-2">
                            {project.brief}
                        </p>
                    )}
                    {currentStageLabel && (
                        <div
                            data-testid="project-header-current-stage"
                            className="mt-3 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm border border-zinc-800 bg-zinc-950 text-zinc-400"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF4400]" />
                            Current · {currentStageLabel}
                        </div>
                    )}
                </div>

                {/* Right: actions */}
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        data-testid="project-header-open-assistant"
                        onClick={onOpenAssistant}
                        className="btn-primary text-xs px-3 py-2 inline-flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        Project Assistant
                    </button>
                    <button
                        type="button"
                        data-testid="project-header-open-more-tools"
                        onClick={onOpenMoreTools}
                        className="btn-secondary text-xs px-3 py-2 inline-flex items-center gap-1.5"
                    >
                        <SlidersHorizontal className="w-3.5 h-3.5" />
                        More Tools
                    </button>
                    {/* Power-user dropdown — hosts the legacy header controls without unmounting them. */}
                    <div className="relative" ref={ref}>
                        <button
                            type="button"
                            data-testid="project-header-more-menu-toggle"
                            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                            className="btn-secondary text-xs px-3 py-2 inline-flex items-center gap-1.5"
                            aria-haspopup="menu"
                            aria-expanded={open}
                        >
                            <MoreHorizontal className="w-3.5 h-3.5" />
                            More
                        </button>
                        {open && (
                            <div
                                data-testid="project-header-more-menu"
                                role="menu"
                                className="absolute right-0 mt-2 min-w-[260px] border border-zinc-800 bg-[#0a0a0a] rounded-sm shadow-xl z-30 p-2 space-y-1"
                            >
                                <div className="label-micro px-2 pb-1">Power tools</div>
                                <div className="flex flex-col gap-1.5 [&_button]:w-full [&_a]:w-full">
                                    {extraControls}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
